import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import MetricDrillDown from '../../components/MetricDrillDown';
import { 
  DollarSign, TrendingUp, Users, Home, Download, Calculator,
  RefreshCw, PiggyBank, Target, ArrowRight, BarChart3, MousePointerClick
} from 'lucide-react';

export default function ROICalculatorPage() {
  const { axiosAuth } = useAuth();
  const [period, setPeriod] = useState('year');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/roi-calculator?period=${period}`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load ROI data');
    }
    setLoading(false);
  };

  const openDrillDown = async (metric) => {
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const res = await api.get(`/reports/roi-calculator/breakdown/${metric}?period=${period}`);
      setDrillData(res.data);
    } catch (err) {
      toast.error('Failed to load breakdown');
      setDrillOpen(false);
    }
    setDrillLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roi-calculator-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            ROI Calculator
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Cost savings analysis: Program vs. Incarceration
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Monthly</SelectItem>
              <SelectItem value="quarter">Quarterly</SelectItem>
              <SelectItem value="year">Annual</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" className="gap-2 rounded-full" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </Button>
        </div>
      </div>

      {data && (
        <>
          {/* Impact Multiplier Hero */}
          <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white overflow-hidden">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <p className="text-emerald-100 text-sm font-medium mb-2">IMPACT MULTIPLIER</p>
                  <p className="text-5xl md:text-6xl font-bold">{data.impact_multiplier}x</p>
                  <p className="text-emerald-100 mt-2">
                    Every $1 invested saves ${data.impact_multiplier} in incarceration costs
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                    <Calculator className="w-10 h-10" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Key Stats — click to drill down */}
          <p className="text-xs text-muted-foreground flex items-center gap-1 -mb-2"><MousePointerClick size={11} /> Tap any card to see the breakdown</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card onClick={() => openDrillDown('total_served')} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="card-total-served">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
                    <Users className="text-sky-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold">{data.participants.total_served}</p>
                <p className="text-xs text-muted-foreground">Total Served</p>
              </CardContent>
            </Card>
            <Card onClick={() => openDrillDown('housing_placements')} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="card-housing-placements">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <Home className="text-emerald-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold">{data.participants.housing_placements}</p>
                <p className="text-xs text-muted-foreground">Housing Placements</p>
              </CardContent>
            </Card>
            <Card onClick={() => openDrillDown('roi_percentage')} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="card-roi-percentage">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                    <PiggyBank className="text-violet-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold">{data.savings.roi_percentage}%</p>
                <p className="text-xs text-muted-foreground">ROI Percentage</p>
              </CardContent>
            </Card>
            <Card onClick={() => openDrillDown('net_savings')} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="card-annual-savings">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                    <TrendingUp className="text-amber-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(data.savings.net_savings_annual)}</p>
                <p className="text-xs text-muted-foreground">Annual Savings</p>
              </CardContent>
            </Card>
          </div>

          {/* Cost Comparison */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                  Cost Comparison
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
                  <div>
                    <p className="font-medium text-red-700">Without Program (Incarceration)</p>
                    <p className="text-sm text-red-600">${data.costs.incarceration_cost_per_year.toLocaleString()}/person/year</p>
                  </div>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(data.costs.cost_without_program)}</p>
                </div>
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
                  <div>
                    <p className="font-medium text-emerald-700">With Our Program</p>
                    <p className="text-sm text-emerald-600">${data.costs.program_cost_per_participant.toLocaleString()}/participant</p>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(data.costs.cost_with_program)}</p>
                </div>
                <div className="flex items-center justify-center py-2">
                  <ArrowRight className="text-muted-foreground" />
                </div>
                <div onClick={() => openDrillDown('net_savings')} className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-xl border-2 border-emerald-200 cursor-pointer hover:shadow-md transition-all" data-testid="net-savings-highlight">
                  <div>
                    <p className="font-bold text-emerald-800 flex items-center gap-1">NET SAVINGS <MousePointerClick size={11} className="text-emerald-600 opacity-60" /></p>
                    <p className="text-sm text-emerald-600">Per participant: {formatCurrency(data.savings.savings_per_participant)}</p>
                  </div>
                  <p className="text-3xl font-bold text-emerald-700">{formatCurrency(data.savings.net_savings_annual)}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                  Long-Term Projections
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">Year 1</span>
                      <span className="text-sm text-emerald-600">{formatCurrency(data.savings.net_savings_annual)}</span>
                    </div>
                    <Progress value={33} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">Year 2 (Cumulative)</span>
                      <span className="text-sm text-emerald-600">{formatCurrency(data.savings.net_savings_annual * 2)}</span>
                    </div>
                    <Progress value={66} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">Year 3 (With Recidivism Reduction)</span>
                      <span className="text-sm text-emerald-600">{formatCurrency(data.savings.projected_3_year_savings)}</span>
                    </div>
                    <Progress value={100} className="h-2" />
                  </div>
                </div>
                <div className="p-4 bg-violet-50 rounded-xl mt-4">
                  <p className="text-sm font-medium text-violet-700 mb-1">Recidivism Reduction Bonus</p>
                  <p className="text-2xl font-bold text-violet-700">{formatCurrency(data.savings.recidivism_reduction_savings)}</p>
                  <p className="text-xs text-violet-600 mt-1">
                    Additional savings from 35% reduced recidivism over 3 years
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Funder Talking Points */}
          <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-slate-50 to-slate-100">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Target className="text-indigo-600" size={18} /> Key Metrics for Funders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <p className="text-3xl font-bold text-indigo-600">{data.impact_multiplier}x</p>
                  <p className="text-sm text-muted-foreground">Return on Investment</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <p className="text-3xl font-bold text-emerald-600">{formatCurrency(data.savings.savings_per_participant)}</p>
                  <p className="text-sm text-muted-foreground">Saved Per Participant</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <p className="text-3xl font-bold text-violet-600">{formatCurrency(data.savings.projected_3_year_savings)}</p>
                  <p className="text-sm text-muted-foreground">3-Year Projected Savings</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <MetricDrillDown open={drillOpen} onOpenChange={setDrillOpen} data={drillData} loading={drillLoading} />
    </div>
  );
}
